import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { getFilteredOptions } from '@/features/vehiclesearch/services';
import type {
    VehicleSearchFilters,
    VehicleDerivative,
    FilteredOptionsResponse,
} from '@/features/vehiclesearch/types/manualsearch.types';
import type { PlanVehicleRuleFormInput } from '../schemas/planVehicleRule.schema';
import { OPTIONS_ERROR_MESSAGE, loadCascade, mergeAvailableOptions } from '../components/planVehicleRuleFormState';

interface Params {
    seedFilters: VehicleSearchFilters;
    mode: 'create' | 'edit';
    setValue: UseFormSetValue<PlanVehicleRuleFormInput>;
}

/**
 * Owns the /filtered-options cascade for the plan vehicle rule form: the filter state that drives
 * the requests, the option/derivative lists they return, and the race guard that keeps a slow
 * earlier response from overwriting a newer one.
 */
export function usePlanVehicleRuleCascade({ seedFilters, mode, setValue }: Params) {
    const [filters, setFilters] = useState<VehicleSearchFilters>(seedFilters);
    // Starts true: the mount effect below always runs a cascade, and the make combobox reads this
    // flag before that first cascade resolves.
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [availableOptions, setAvailableOptions] = useState<FilteredOptionsResponse['available_options']>({});
    const [matchingDerivatives, setMatchingDerivatives] = useState<VehicleDerivative[]>([]);
    const [optionsError, setOptionsError] = useState<string | null>(null);

    // Monotonic request id shared by the mount cascade, Reset, and every filter-change fetch. Only
    // the newest request may write the option, derivative, and loading state, so a slow earlier
    // response cannot overwrite a newer one (and re-enable the derivative picker with a derivative
    // that is invalid for the current filters).
    const requestIdRef = useRef(0);

    const applyCascade = useCallback(async (targetFilters: VehicleSearchFilters, cascadeMode: 'create' | 'edit') => {
        const requestId = ++requestIdRef.current;
        setIsLoadingOptions(true);
        try {
            const { options, derivatives } = await loadCascade(targetFilters, cascadeMode);
            if (requestId !== requestIdRef.current) return;
            setAvailableOptions(options);
            setMatchingDerivatives(derivatives);
            setOptionsError(null);
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            console.error('[PlanVehicleRuleForm] Failed to load options', err);
            setOptionsError(OPTIONS_ERROR_MESSAGE);
        } finally {
            if (requestId === requestIdRef.current) setIsLoadingOptions(false);
        }
    }, []);

    useEffect(() => {
        void applyCascade(seedFilters, mode);
        // Supersede the in-flight cascade on unmount so a late response cannot write state.
        return () => { requestIdRef.current += 1; };
    }, [seedFilters, mode, applyCascade]);

    const handleFilterChange = async (key: keyof VehicleSearchFilters, value: string) => {
        const retained: VehicleSearchFilters = {
            ...(filters.transmission_id ? { transmission_id: filters.transmission_id } : {}),
            ...(filters.fuel_type_id ? { fuel_type_id: filters.fuel_type_id } : {}),
        };
        let newFilters: VehicleSearchFilters;

        if (key === 'make_id') {
            newFilters = value ? { make_id: value, ...retained } : { ...retained };
            setValue('make_id', value, { shouldDirty: true });
            setValue('model_id', '', { shouldDirty: true });
            setValue('derivative_id', '', { shouldDirty: true });
            setMatchingDerivatives([]);
        } else if (key === 'model_id') {
            newFilters = value
                ? { make_id: filters.make_id, model_id: value, ...retained }
                : { make_id: filters.make_id, ...retained };
            setValue('model_id', value, { shouldDirty: true });
            setValue('derivative_id', '', { shouldDirty: true });
            setMatchingDerivatives([]);
        } else if (key === 'transmission_id' || key === 'fuel_type_id') {
            newFilters = { ...filters };
            if (value) {
                newFilters[key] = value;
            } else {
                delete newFilters[key];
            }
            setValue(key, value, { shouldDirty: true });
            // The derivative list narrows by both keys; drop it before the fetch so a stale
            // pick cannot survive into derivative_id while the response is in flight.
            setValue('derivative_id', '', { shouldDirty: true });
            setMatchingDerivatives([]);
            if (!(filters.make_id && filters.model_id)) {
                setFilters(newFilters);
                return;
            }
        } else {
            newFilters = { ...filters };
            if (value) {
                newFilters[key] = value;
            } else {
                delete newFilters[key];
            }
        }

        setFilters(newFilters);

        // Claim the newest request id before awaiting; every write below is gated on still holding
        // it, so an earlier response resolving late is dropped rather than applied over a newer one.
        const requestId = ++requestIdRef.current;
        // Global lists never narrow, so a retained fuel type or transmission with no make is
        // still the reset state: refetch makes without raising the loading flag.
        const isReset = !newFilters.make_id;

        if (!isReset) setIsLoadingOptions(true);

        try {
            const response = isReset
                ? await getFilteredOptions({})
                : await getFilteredOptions(newFilters, { includeDerivatives: true });
            if (requestId !== requestIdRef.current) return;
            setOptionsError(null);
            if (isReset) {
                setAvailableOptions(response.available_options);
                return;
            }
            setAvailableOptions((prev) => mergeAvailableOptions(prev, response.available_options));
            // Unconditional set: a narrowing that produces zero derivatives must clear a stale
            // list (and re-disable the combobox), not leave a previous result showing.
            setMatchingDerivatives(response.matching_vehicles ?? []);
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            console.error('[PlanVehicleRuleForm] Failed to load options', err);
            setOptionsError(OPTIONS_ERROR_MESSAGE);
        } finally {
            if (requestId === requestIdRef.current) setIsLoadingOptions(false);
        }
    };

    /** Clear (create mode): drop the filters and the derivative list without refetching. */
    const clearCascade = useCallback(() => {
        setFilters({});
        setMatchingDerivatives([]);
    }, []);

    /**
     * Reset (edit mode): restoring the values alone would leave the edited cascade's option and
     * derivative lists in place, so a restored make/model absent from them renders as a cleared
     * placeholder while the payload already says otherwise. Replay the seed cascade to replace both.
     */
    const replaySeedCascade = useCallback(() => {
        setFilters(seedFilters);
        setMatchingDerivatives([]);
        void applyCascade(seedFilters, mode);
    }, [seedFilters, mode, applyCascade]);

    return {
        filters,
        availableOptions,
        matchingDerivatives,
        isLoadingOptions,
        optionsError,
        handleFilterChange,
        clearCascade,
        replaySeedCascade,
    };
}
